import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const services = [
  {
    num: "01",
    title: "Branding",
    desc: "We build powerful brand identities that tell your story, connect with your audience, and set you apart in a crowded marketplace.",
  },
  {
    num: "02",
    title: "Web Design/Development",
    desc: "From stunning designs to seamless functionality, we create websites that captivate visitors and convert them into loyal customers.",
  },
  {
    num: "03",
    title: "Content Creation",
    desc: "Engaging visuals, compelling copy, and strategic storytelling that amplifies your brand voice across every platform and channel.",
  },
  {
    num: "04",
    title: "SEO/AEO",
    desc: "Data-driven search engine and answer engine optimization that boosts your visibility, drives organic traffic, and puts you ahead of the competition.",
  },
  {
    num: "05",
    title: "Performance Marketing",
    desc: "Results-focused advertising campaigns across Google, Meta, and beyond that maximize your ROI and accelerate business growth.",
  },
];

export default function ServicesSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const goNext = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % services.length);
  };

  const goPrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + services.length) % services.length);
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  const service = services[current];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-zinc-950" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#667eea]/5 to-[#764ba2]/5 rounded-full blur-[150px]" />
      </div>

      <div className="container mx-auto px-6 md:px-12 max-w-[1400px] relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">
            Capabilities
          </p>
          <h2 className="font-syne text-4xl md:text-6xl font-bold tracking-tight text-white">
            Our <span className="gradient-text">Services</span>
          </h2>
        </motion.div>

        {/* Two-column layout: Video left, Slider right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          {/* Left: Video */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden bg-zinc-950 border border-white/5 min-h-[400px] md:min-h-[500px]"
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="w-full h-full object-cover absolute inset-0"
              data-testid="services-video"
            >
              <source
                src="/assets/services.mp4"
                type="video/mp4"
              />
            </video>
          </motion.div>

          {/* Right: Service Slider */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="bg-black border border-white/5 flex flex-col justify-between min-h-[400px] md:min-h-[500px]"
          >
            {/* Service Content */}
            <div className="flex-1 p-8 md:p-12 flex flex-col justify-center relative overflow-hidden">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={current}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`service-slide-${service.num}`}
                >
                  <span className="text-6xl md:text-8xl font-syne font-bold text-zinc-800 block mb-6">
                    {service.num}
                  </span>
                  <h3 className="font-syne text-2xl md:text-3xl font-bold text-white mb-5">
                    {service.title}
                  </h3>
                  <p className="text-base font-light tracking-wide text-zinc-400 leading-relaxed max-w-[450px]">
                    {service.desc}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Navigation Controls */}
            <div className="border-t border-white/5 px-8 md:px-12 py-5 flex items-center justify-between">
              {/* Dots */}
              <div className="flex items-center gap-1">
                {services.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setDirection(i > current ? 1 : -1);
                      setCurrent(i);
                    }}
                    aria-label={`Go to service ${i + 1}`}
                    data-testid={`service-dot-${i}`}
                    className={`p-2 rounded-full transition-all duration-300 ${
                      i === current
                        ? "bg-gradient-to-r from-[#667eea] to-[#764ba2]"
                        : "bg-zinc-700 hover:bg-zinc-500"
                    }`}
                    style={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div
                      className={`rounded-full transition-all ${
                        i === current
                          ? "w-2 h-2"
                          : "w-1 h-1"
                      }`}
                      style={{
                        backgroundColor: i === current ? 'transparent' : 'inherit'
                      }}
                    />
                  </button>
                ))}
              </div>

              {/* Arrows */}
              <div className="flex items-center gap-3">
                <button
                  onClick={goPrev}
                  aria-label="Previous service"
                  data-testid="service-prev-button"
                  className="w-10 h-10 border border-white/10 flex items-center justify-center text-zinc-500 hover:text-white hover:border-[#667eea]/50 transition-all duration-300"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={goNext}
                  aria-label="Next service"
                  data-testid="service-next-button"
                  className="w-10 h-10 border border-white/10 flex items-center justify-center text-zinc-500 hover:text-white hover:border-[#667eea]/50 transition-all duration-300"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
